// Where to start — a guitarist's route through the app (Sep 2026, user ask:
// "a tutorial for the app itself, for guitarists… start with drop 2, do some
// analysis on the corpus and list the most common chord types so you learn
// those inversions first, mention the synonyms so you don't need that many
// shapes, then the most common progressions with their inversions").
//
// The numbers are LIVE: the page reads the Jazz 1460 through the same
// repository the Corpus page uses and reshapes it with corpusLessons.ts, so
// the syllabus is whatever the repertoire says, not a claim typed in once. The
// diagrams are the app's own — generateChordVoicing for the inversions,
// generateProgressionsFromSteps for the voice-led cadences — on the reader's
// current string set and family, so what they see here is what the workbench
// will play. Every "try it" link carries a recipe (tuneRecipe.ts).

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { recipeUrl } from '@/components/tuneRecipe';
import {
  chordTypeShares,
  commonProgressions,
  shapeFamilies,
  type CommonProgression,
  type ShapeFamily,
  type TypeShare,
} from '@/data/corpusLessons';
import { useCorpusRepository } from '@/data/useCorpusRepository';
import type { CorpusStats } from '@/music/corpus';
import { generateProgressionsFromSteps } from '@/music/progressions';
import { generateChordVoicing, voicingIdentityLabel, type Inversion } from '@/music/voicing';
import type { VoicingFamilySelection } from '@/music/voicingFamilies';
import { primaryVoicingFamily } from '@/music/voicingFamilies';
import { APP_ROUTES } from '@/navigation';
import { useStore } from '@/state/store';
import '@/learn.css';

const pct = (share: number, dp = 0) => `${(100 * share).toFixed(dp)}%`;
const fmt = (n: number) => n.toLocaleString('en-GB');

/** How many of the top types it takes to pass a share of the corpus. */
function coverage(shares: TypeShare[], target: number): TypeShare[] {
  const idx = shares.findIndex((s) => s.cumulative >= target);
  return idx < 0 ? shares : shares.slice(0, idx + 1);
}

function ShareBars({ shares }: { shares: TypeShare[] }) {
  const max = Math.max(...shares.map((s) => s.share), 0.01);
  return (
    <div className="start-shares" data-testid="start-shares">
      {shares.map((s) => (
        <div key={s.type} className="start-share-row">
          <span className="start-share-label">{s.symbol}</span>
          <span className="start-share-track">
            <span className="start-share-fill" style={{ width: `${(100 * s.share) / max}%` }} />
          </span>
          <span className="start-share-value">{pct(s.share, 1)}</span>
          <span className="start-share-cum muted">{pct(s.cumulative)}</span>
        </div>
      ))}
    </div>
  );
}

function Inversions({
  type,
  stringSet,
  family,
  highlightAvoid,
}: {
  type: TypeShare;
  stringSet: 'middle' | 'upper';
  family: VoicingFamilySelection;
  highlightAvoid: boolean;
}) {
  const physical = primaryVoicingFamily(family);
  return (
    <div className="start-inversions" data-testid="start-inversions">
      <h3>{type.symbol}</h3>
      <div className="assignment-grid">
        {([0, 1, 2, 3] as Inversion[]).map((inversion) => {
          const voicing = generateChordVoicing('C', type.type, inversion, stringSet, physical);
          if (!voicing) return null;
          return (
            <PlayableDiagram
              key={inversion}
              chord={{
                fingering: voicing,
                rootDisplay: 'C',
                chordType: type.type,
                symbol: voicing.symbol,
                inversion: voicing.inversion,
                stringSet,
              }}
              caption={voicingIdentityLabel(voicing, 'C', type.type)}
              showTitle={false}
              highlightAvoid={highlightAvoid}
            />
          );
        })}
      </div>
    </div>
  );
}

function ProgressionRow({
  progression,
  stringSet,
  family,
  avoidB9,
  freeStringSet,
}: {
  progression: CommonProgression;
  stringSet: 'middle' | 'upper';
  family: VoicingFamilySelection;
  avoidB9: boolean;
  freeStringSet: boolean;
}) {
  const patterns = useMemo(
    () =>
      generateProgressionsFromSteps(progression.steps, stringSet, {
        avoidB9,
        freeStringSet,
        voicingFamily: family,
        limit: 2,
      }),
    [progression, stringSet, avoidB9, freeStringSet, family],
  );
  return (
    <div className="start-progression" data-testid="start-progression">
      <div className="start-progression-head">
        <h3>{progression.label}</h3>
        <span className="muted">{fmt(progression.count)} times</span>
      </div>
      {patterns.map((pattern, pi) => (
        <div key={pi} className="progression-chords">
          {pattern.chords.map((chord, ci) => (
            <span key={ci} className="start-progression-cell">
              <PlayableDiagram
                chord={{
                  fingering: chord.voicing,
                  rootDisplay: chord.root,
                  chordType: chord.type,
                  symbol: chord.symbol,
                  inversion: chord.inversion,
                  stringSet: chord.stringSet,
                }}
                caption={
                  <>
                    <span className="card-chord">{chord.symbol}</span>
                    {voicingIdentityLabel(chord.voicing, chord.root, chord.type)}
                  </>
                }
                showTitle={false}
                highlightAvoid={avoidB9}
              />
              {ci < pattern.chords.length - 1 && <span className="progression-arrow">→</span>}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function Families({ families }: { families: ShapeFamily[] }) {
  return (
    <ul className="start-families" data-testid="start-families">
      {families.map((f) => (
        <li key={f.shape.type}>
          <strong>{f.shape.symbol}</strong>
          <span className="muted"> · {pct(f.share)} of all chords</span>
          {f.covers.length > 0 && (
            <div className="start-family-covers">
              {f.covers.map((c) => (
                <span key={c.type.type} className="start-family-cover">
                  {c.example}
                </span>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export function GuitarStartContent({ stats }: { stats: CorpusStats }) {
  const stringSet = useStore((s) => s.stringSet);
  const selectedFamily = useStore((s) => s.voicingFamily);
  const family = selectedFamily === 'joe-pass' ? 'drop-2' : selectedFamily;
  const avoidB9 = useStore((s) => s.avoidB9);
  const freeStringSet = useStore((s) => s.freeStringSet);

  const shares = useMemo(() => chordTypeShares(stats, 10), [stats]);
  const families = useMemo(() => shapeFamilies(shares), [shares]);
  const core = useMemo(() => coverage(shares, 0.85), [shares]);
  const trigrams = useMemo(() => commonProgressions(stats.trigrams, 3), [stats]);
  const bigrams = useMemo(
    () =>
      commonProgressions(stats.bigrams, 8)
        .filter((b) => !trigrams.some((t) => t.label.startsWith(b.label)))
        .slice(0, 3),
    [stats, trigrams],
  );
  const shapesToLearn = families.slice(0, 4).map((f) => f.shape);

  return (
    <>
      <section id="use" className="drop2-section" aria-labelledby="start-use-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">02</span>
          <div>
            <h2 id="start-use-heading">Common chord types</h2>
          </div>
        </div>
        <p className="drop2-lede" data-testid="start-coverage">
          The first {core.length} types make up {pct(core[core.length - 1]?.cumulative ?? 0)} of all
          chords. Learn these first.
        </p>
        <ShareBars shares={shares} />
        <p className="drop2-hint">
          Counted in performance order. The right-hand column is the running total. All of these
          types are in the <Link to={APP_ROUTES.chordLibrary}>Chord library</Link>. More statistics
          are on the <Link to={APP_ROUTES.corpus}>Corpus</Link> page.
        </p>
      </section>

      <section id="shapes" className="drop2-section" aria-labelledby="start-shapes-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">03</span>
          <div>
            <h2 id="start-shapes-heading">Chords that share a shape</h2>
          </div>
        </div>
        <p className="drop2-lede">
          A ninth chord voicing omits the root, so Cmaj9 has the same notes as Em7. C6 has the same
          notes as Am7. These pairs use the same shapes on the guitar.
        </p>
        <Families families={families} />
      </section>

      <section id="inversions" className="drop2-section" aria-labelledby="start-inv-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">04</span>
          <div>
            <h2 id="start-inv-heading">Inversions</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Each chord type has four drop 2 inversions on a string set, one for each chord tone on
          top. Tap a diagram to hear it. Shown on the{' '}
          {stringSet === 'middle' ? 'A–D–G–B' : 'D–G–B–E'} strings. The Chords menu on the workbench
          changes the string set.
        </p>
        {shapesToLearn.map((type) => (
          <Inversions
            key={type.type}
            type={type}
            stringSet={stringSet}
            family={family}
            highlightAvoid={avoidB9}
          />
        ))}
        <p className="drop2-hint">
          The <Link to={`${APP_ROUTES.studies}/inversions`}>Drop 2 Top Voices</Link> study covers
          every chord type with each chord tone on top.
        </p>
      </section>

      <section id="progressions" className="drop2-section" aria-labelledby="start-prog-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">05</span>
          <div>
            <h2 id="start-prog-heading">Common progressions</h2>
          </div>
        </div>
        <p className="drop2-lede" data-testid="start-iiv">
          {pct(stats.iiVShare)} of chord changes are ii to V, and{' '}
          {pct(stats.tunes ? stats.tunesWithIiVI / stats.tunes : 0)} of tunes contain a complete
          ii–V–I. The most common two- and three-chord progressions are shown below in C, each
          voiced two ways.
        </p>
        {[...trigrams, ...bigrams].map((p) => (
          <ProgressionRow
            key={p.label}
            progression={p}
            stringSet={stringSet}
            family={family}
            avoidB9={avoidB9}
            freeStringSet={freeStringSet}
          />
        ))}
        <p className="drop2-hint">
          The <Link to={APP_ROUTES.progressions}>Progressions</Link> page plays these progressions
          in a loop. The{' '}
          <Link to={`${APP_ROUTES.studies}/voice-leading-chord-scales`}>chord-scale study</Link>{' '}
          voices them with a fixed top line.
        </p>
      </section>
    </>
  );
}

const FIRST_TUNES = [
  {
    title: 'All Of Me',
    note: 'One key, one chord per bar, many ii–V–Is. Comping voicings, nearest grip.',
    to: recipeUrl(
      { title: 'All Of Me' },
      { view: 'chart', lens: 'comping', engine: 'pathfinder-v1', family: 'drop-2' },
    ),
  },
  {
    title: 'Autumn Leaves',
    note: 'Relative minor and major keys. Harmonic analysis shown under each chord.',
    to: recipeUrl(
      { title: 'Autumn Leaves' },
      { view: 'chart', lens: 'comping', analysis: true, family: 'drop-2' },
    ),
  },
  {
    title: 'Blue Bossa',
    note: 'Sixteen bars with one key change. Whole-line voice leading, bossa nova backing.',
    to: recipeUrl(
      { title: 'Blue Bossa' },
      { view: 'chart', lens: 'comping', engine: 'pathfinder-global-v1', style: 'bossa' },
    ),
  },
];

export function GuitarStartPage() {
  const { repository, error } = useCorpusRepository();
  const cmaj7 = useMemo(() => generateChordVoicing('C', 'maj7', 0, 'middle', 'drop-2'), []);
  return (
    <article className="drop2-page start-page">
      <nav className="learn-breadcrumb" aria-label="Breadcrumb">
        <Link to={APP_ROUTES.learn}>Learn</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Where to start</span>
      </nav>

      <header className="drop2-hero">
        <p className="learn-eyebrow">For guitarists</p>
        <h1>Where to start</h1>
        <p>
          Drop 2 voicings, the most common chord types and progressions, and a first tune to play
          with the band.
        </p>
      </header>

      <nav className="drop2-toc" aria-label="On this page">
        <a href="#drop2">Drop 2</a>
        <a href="#use">Chord types</a>
        <a href="#shapes">Shared shapes</a>
        <a href="#inversions">Inversions</a>
        <a href="#progressions">Progressions</a>
        <a href="#tune">Example tunes</a>
      </nav>

      <section id="drop2" className="drop2-section" aria-labelledby="start-drop2-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">01</span>
          <div>
            <h2 id="start-drop2-heading">Drop 2</h2>
          </div>
        </div>
        <div className="drop2-two-column">
          <div>
            <p>
              A drop 2 voicing takes a four-note chord in close position and lowers the second voice
              from the top by an octave. The result fits on four adjacent strings. Guitar voicings
              in the workbench use drop 2 by default.
            </p>
            <p>
              Each chord has four inversions on each of two string sets, named by the note on top.{' '}
              <Link to={APP_ROUTES.drop2Intro}>Drop 2</Link> explains the construction step by step.{' '}
              <Link to={APP_ROUTES.drop3Intro}>Drop 3</Link> covers the wider family.
            </p>
          </div>
          {cmaj7 && (
            <PlayableDiagram
              chord={{
                fingering: cmaj7,
                rootDisplay: 'C',
                chordType: 'maj7',
                symbol: 'Cmaj7',
                inversion: 0,
                stringSet: 'middle',
              }}
              caption="Cmaj7 · 7th on top"
              showTitle={false}
            />
          )}
        </div>
      </section>

      {repository ? (
        <GuitarStartContent stats={repository.stats} />
      ) : (
        <p className="muted" data-testid="start-loading">
          {error ?? 'Loading…'}
        </p>
      )}

      <section id="tune" className="drop2-section" aria-labelledby="start-tune-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">06</span>
          <div>
            <h2 id="start-tune-heading">Example tunes</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Each link opens the workbench with these settings applied. Switch the voicings off and on
          to compare the chart with the diagrams.
        </p>
        <div className="learn-index-list" data-testid="start-tunes">
          {FIRST_TUNES.map((t) => (
            <Link key={t.title} to={t.to}>
              <strong>{t.title}</strong>
              <span>{t.note}</span>
            </Link>
          ))}
        </div>
        <p className="drop2-hint">
          <Link to={APP_ROUTES.guide}>How the app works</Link> covers the band, reharmonisation and
          the voice-leading options, with more examples.
        </p>
      </section>
    </article>
  );
}
